/**
 * 检查项记录管理 - 常量定义
 * 来源：Web管理后台-检查项记录管理.md 二、五节
 */
import {
  CheckRecordStatus,
  CheckStatusAction,
  AiAnalysisStatus
} from '../api/check'

// ------------------------------------------------------------
// 检查项记录状态（0~5）
// ------------------------------------------------------------

export const CHECK_STATUS_LABEL: Record<CheckRecordStatus, string> = {
  0: '待分析',
  1: 'AI审核中',
  2: '合格',
  3: '不合格',
  4: '人工审核中',
  5: '复议审核中'
}

/**
 * 状态色板 — 对齐 web后台.html POINT_STATUS 配色（line 454-461）
 *   pending(0) yellow / ai(1) blue / pass(2) green / fixing(3) red /
 *   manual(4) slate / reconsider(5) purple
 */
export const CHECK_STATUS_COLOR: Record<CheckRecordStatus, string> = {
  0: '#B07700', // yellow
  1: '#3B7DEB', // blue
  2: '#10A66B', // green
  3: '#E84C4C', // red
  4: '#475569', // slate
  5: '#7B5BEB' // purple
}

export const CHECK_STATUS_BG: Record<CheckRecordStatus, string> = {
  0: '#FFF6DB',
  1: '#E9F2FF',
  2: '#E7F8EF',
  3: '#FEECEC',
  4: '#EEF1F5',
  5: '#F1EBFF'
}

export const CHECK_STATUS_FILTER_OPTIONS: Array<{
  value: CheckRecordStatus
  label: string
}> = [
  { value: 0, label: '待分析' },
  { value: 1, label: 'AI审核中' },
  { value: 2, label: '合格' },
  { value: 3, label: '不合格' },
  { value: 4, label: '人工审核中' },
  { value: 5, label: '复议审核中' }
]

// ------------------------------------------------------------
// 行操作（按当前状态动态展示）
// ------------------------------------------------------------

export interface CheckActionConfig {
  action: CheckStatusAction
  label: string
  /** 是否需要二次确认（无 remark 输入） */
  confirmOnly: boolean
  /** 提示文案 */
  confirmTip?: string
  /** 是否危险样式 */
  danger?: boolean
}

export const CHECK_ACTIONS_BY_STATUS: Partial<
  Record<CheckRecordStatus, CheckActionConfig[]>
> = {
  // 合格状态下,管理员可直接驳回(走 review_reject 接口,转回不合格)
  // 普通成员看不到 — List/Detail 的 isAdmin 过滤已自动只允许 submit_* 通过
  2: [
    {
      action: 'review_reject',
      label: '驳回',
      confirmOnly: false,
      confirmTip: '请填写驳回原因',
      danger: true
    }
  ],
  3: [
    {
      action: 'submit_rectify',
      label: '提交整改',
      confirmOnly: false,
      confirmTip: '请填写整改说明'
    },
    {
      action: 'submit_appeal',
      label: '发起复议',
      confirmOnly: false,
      confirmTip: '请填写复议理由'
    }
  ],
  4: [
    {
      action: 'review_pass',
      label: '审核通过',
      confirmOnly: true,
      confirmTip: '确认通过人工审核？'
    },
    {
      action: 'review_reject',
      label: '驳回整改',
      confirmOnly: false,
      confirmTip: '请填写驳回原因',
      danger: true
    }
  ],
  5: [
    {
      action: 'appeal_pass',
      label: '复议通过',
      confirmOnly: true,
      confirmTip: '确认通过复议？'
    },
    {
      action: 'appeal_reject',
      label: '驳回复议',
      confirmOnly: false,
      confirmTip: '请填写驳回原因',
      danger: true
    }
  ]
}

// ------------------------------------------------------------
// AI 分析状态枚举
// ------------------------------------------------------------

export const AI_STATUS_LABEL: Record<AiAnalysisStatus, string> = {
  0: '合格',
  1: '不合格',
  2: '数据质量问题',
  3: '解析异常'
}

export const AI_STATUS_COLOR: Record<AiAnalysisStatus, string> = {
  0: '#52C41A',
  1: '#F5222D',
  2: '#FA8C16',
  3: '#8C8C8C'
}

// ------------------------------------------------------------
// 操作日志 action 标签映射
// ------------------------------------------------------------

export const ACTION_LABEL: Record<string, string> = {
  create: '创建记录',
  ai_analysis: 'AI分析完成',
  submit_rectify: '提交整改',
  submit_appeal: '发起复议',
  review_pass: '审核通过',
  review_reject: '驳回整改',
  appeal_pass: '复议通过',
  appeal_reject: '驳回复议'
}

// ------------------------------------------------------------
// 顶部统计卡片配置（6 个）
// 标签/颜色对齐 web后台.html 设计稿
// ------------------------------------------------------------

export type CheckStatsKey =
  | 'total'
  | 'passed'
  | 'failed'
  | 'pending'
  | 'manual_reviewing'
  | 'appealing'

export interface CheckStatsCardConfig {
  key: CheckStatsKey
  label: string
  icon: string
  color: string
  bg: string
}

export const CHECK_STATS_CARDS: CheckStatsCardConfig[] = [
  {
    key: 'total',
    label: '总检查项数量',
    icon: 'appstore',
    color: '#3B7DEB',
    bg: '#E9F2FF'
  },
  {
    key: 'passed',
    label: '合格数量',
    icon: 'check-circle',
    color: '#10A66B',
    bg: '#E7F8EF'
  },
  {
    key: 'failed',
    label: '不合格数量',
    icon: 'close-circle',
    color: '#E84C4C',
    bg: '#FEECEC'
  },
  {
    key: 'pending',
    label: '待分析数量',
    icon: 'clock-circle',
    color: '#F59E0B',
    bg: '#FEF3DB'
  },
  {
    key: 'appealing',
    label: '需复议审核数量',
    icon: 'exclamation-circle',
    color: '#8B5CF6',
    bg: '#F1EBFF'
  },
  {
    key: 'manual_reviewing',
    label: '需人工审核数量',
    icon: 'audit',
    color: '#475569',
    bg: '#EEF1F5'
  }
]
